// import { CUSTOMER_CHAT_COOKIE_NAME } from "$types/chat"
import {
  ATTRIBUTE_NAME,
  LOCAL_STORAGE_KEY,
  ACTIVE_CLASS_NAME,
  SIDEBAR_DROPDOWN_INITIALIZED_LOCAL_STORAGE_KEY,
  SIDEBAR_CHATS_CONTAINER_SCROLL_LOCAL_STORAGE_KEY,
} from "./constants"

declare global {
  interface Window {
    toggleSidebar: () => void
    toggleSidebarDropdown: (key: string) => void
    initSidebarDropdowns: () => void
    onSidebarSearchInputChange: (event: Event) => void
    onSidebarSearchInputResetButtonOnClick: () => void
    resetSidebarChatSectionScroll: () => void
    saveSidebarScroll: () => void
  }
}

const getLocalStorageSidebarDropdownKey = (dropdownKey: string) => `sidebar-dropdown-closed-${dropdownKey}`

const scrollToSidebarActiveItem = () => {
  const activeItem = document.querySelector(`.${ACTIVE_CLASS_NAME}`)
  if (activeItem) {
    activeItem.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "start"
    })
  }
}

window.toggleSidebar = () => {
  const html = document.documentElement
  if (!html) {
    return
  }

  const isClosed = html.hasAttribute(ATTRIBUTE_NAME)
  if (isClosed) {
    html.removeAttribute(ATTRIBUTE_NAME)
    localStorage.removeItem(LOCAL_STORAGE_KEY)
  } else {
    html.setAttribute(ATTRIBUTE_NAME, "")
    localStorage.setItem(LOCAL_STORAGE_KEY, "true")
  }

  handleSidebarDropdownActiveElement()
}

const handleSidebarDropdownActiveElement = () => {  
  const html = document.documentElement
  if (!html) {
    return
  }

  const isSidebarClosed = html.hasAttribute(ATTRIBUTE_NAME)
  
  const items = document.querySelectorAll(".sidebar__menu > li")
  const active = document.querySelectorAll(`li.${ACTIVE_CLASS_NAME}`)

  if (!items.length || !active.length) {
    return
  }

  const dropdownItem = Array.from(active).find(e => !!e.getAttribute("data-sidebar-dropdown-target"))
  if (!dropdownItem) {
    return
  }
  
  const key = dropdownItem.getAttribute("data-sidebar-dropdown-target")
  if (!key) {
    return
  }

  const dropdownParent = document.querySelector(`[data-sidebar-dropdown-trigger="${key}"]`)
  if (!dropdownParent) {
    return
  }
  
  if (isSidebarClosed) {
    dropdownParent.classList.add(ACTIVE_CLASS_NAME)
  } else {
    const isDropdownClosed = dropdownParent.classList.contains("closed")
    if (!isDropdownClosed) {
      dropdownParent.classList.remove(ACTIVE_CLASS_NAME)
    }
  }
}

window.toggleSidebarDropdown = (key) => {
  const trigger = document.querySelector(`[data-sidebar-dropdown-trigger="${key}"]`)
  const elements = Array.from(document.querySelectorAll(`[data-sidebar-dropdown-target="${key}"]`))
  if (!trigger || !elements) {
    return
  }

  const localStorageKey = getLocalStorageSidebarDropdownKey(key)

  const isClosed = trigger.classList.contains("closed")

  if (isClosed) {
    localStorage.removeItem(localStorageKey)
  } else {
    localStorage.setItem(localStorageKey, "true")
  }
  
  const items = [...elements, trigger]
  items.forEach((element) => {
    element.classList.toggle("closed", !isClosed)
  })
  const activeElement = elements.find(e => e.classList.contains(ACTIVE_CLASS_NAME))
  if (!activeElement) {
    return
  }

  if (isClosed) {
    // opening the dropdown
    trigger.classList.remove(ACTIVE_CLASS_NAME)
  } else {
    // closing the dropdown
    trigger.classList.add(ACTIVE_CLASS_NAME)
  }
}

document.addEventListener("DOMContentLoaded", () => {
  localStorage.removeItem(SIDEBAR_DROPDOWN_INITIALIZED_LOCAL_STORAGE_KEY)
})

window.initSidebarDropdowns = () => {
  const html = document.documentElement

  if (!html) {
    return
  }

  const isFirstLoad = !localStorage.getItem(SIDEBAR_DROPDOWN_INITIALIZED_LOCAL_STORAGE_KEY)

  const sidebarDropdowns = html.querySelectorAll("[data-sidebar-dropdown-trigger]")

  if (isFirstLoad) {
    localStorage.setItem(SIDEBAR_DROPDOWN_INITIALIZED_LOCAL_STORAGE_KEY, "true")
  }

  sidebarDropdowns.forEach(element => {
    const key = element.getAttribute("data-sidebar-dropdown-trigger")
    
    if (key) {
      if (isFirstLoad) {
        localStorage.removeItem(getLocalStorageSidebarDropdownKey(key))
      } else {
        const trigger = document.querySelector(`[data-sidebar-dropdown-trigger="${key}"]`)
        const elements = Array.from(document.querySelectorAll(`[data-sidebar-dropdown-target="${key}"]`))
    
        if (!trigger || !elements) {
          return
        }
    
        const dropdownClosed = localStorage.getItem(getLocalStorageSidebarDropdownKey(key))
  
        if (dropdownClosed) {
          const items = [...elements, trigger]
          items.forEach((element) => {
            if (!element.classList.contains("closed")) {
              element.classList.add("closed")
            }
          })
  
          const activeElement = elements.find(e => e.classList.contains(ACTIVE_CLASS_NAME))
          if (!activeElement) {
            return
          }
  
          if (dropdownClosed) {
            trigger.classList.remove(ACTIVE_CLASS_NAME)
          }
        }
      }
    }
  })
}

const updateNoChatsFoundMessageVisibility = (show: boolean) => {
  const noResultsFoundIndicator = document.querySelector("#no-chats-found") as HTMLDivElement
  if (noResultsFoundIndicator) {
    noResultsFoundIndicator.style.display = show ? "flex" : "none"
  }
}


let sidebarSearchTimeout: ReturnType<typeof setTimeout>

window.onSidebarSearchInputChange = (event) => {
  clearTimeout(sidebarSearchTimeout)

  sidebarSearchTimeout = setTimeout(() => {
    const value = (event.target as HTMLInputElement).value

    const items = document.querySelectorAll("[data-search-value]")
    const sections = document.querySelectorAll("[data-chat-section]")

    let withResults = false

    items.forEach(i => {
      const item = i as HTMLDivElement
      const name = item.getAttribute("data-search-value")
      if (!name) return

      const visible = value === "" || name.toLowerCase().includes(value.toLowerCase())
      item.style.display = visible ? "flex" : "none"

      if (visible) {
        withResults = true
      }
    })

    sections.forEach(section => {
      const visibleItems = section.querySelectorAll("[data-search-value]:not([style*='display: none'])")
      const sectionEl = section as HTMLDivElement

      sectionEl.style.display = visibleItems.length > 0 ? "flex" : "none"
    })

    updateNoChatsFoundMessageVisibility(!withResults)

    if (value === "") {
      scrollToSidebarActiveItem()
    }
  }, 500)
}

window.onSidebarSearchInputResetButtonOnClick = () => {
  const items = document.querySelectorAll("[data-search-value]")
  const sections = document.querySelectorAll("[data-chat-section]")

  items.forEach(i => {
    const item = i as HTMLDivElement
    const name = item.getAttribute("data-search-value")
    if (!name) return
    item.style.display = "flex"
  })

  sections.forEach(section => {
    const sectionEl = section as HTMLDivElement
    sectionEl.style.display = "flex"
  })

  updateNoChatsFoundMessageVisibility(false)

  scrollToSidebarActiveItem()
}

export {
  handleSidebarDropdownActiveElement
}

window.saveSidebarScroll = () => {
  const chatContainer = document.querySelector(".sidebar__chats-section .sidebar__menu")

  if (chatContainer) {
    localStorage.setItem(SIDEBAR_CHATS_CONTAINER_SCROLL_LOCAL_STORAGE_KEY, chatContainer.scrollTop.toString())
  }
}

window.resetSidebarChatSectionScroll = () => {
  localStorage.setItem(SIDEBAR_CHATS_CONTAINER_SCROLL_LOCAL_STORAGE_KEY, "0")
}
